import type { components } from '../api/generated/schema';

// Admin domain types — backend remains the source of truth for patient/treatment payloads.

export type TreatmentStatus = components['schemas']['TreatmentStatus'];
export type StageKind = components['schemas']['StageKind'];
export type StageStatus = components['schemas']['StageStatus'];
export type DeliveryStatus = 'pending' | 'preparing_shipment' | 'shipped' | 'delivered' | 'cancelled';
export type PaymentStatus = 'pending' | 'submitted_by_patient' | 'confirmed' | 'rejected';
export type CycleProcedureStatus = components['schemas']['CycleProcedureStatus'];
export type JawType = 'upper' | 'lower';
export type ChatSenderRole = 'patient' | 'admin';
export type ChatMessageType = 'text' | 'voice' | 'file';
export type NotificationChannel = 'email' | 'push';
export type NotificationSource = 'system' | 'admin';
export type NotificationCategory = 'chat' | 'payment' | 'delivery' | 'manual';
export type NotificationDeliveryStatus = 'pending' | 'sent' | 'failed' | 'skipped';
export type NotificationTargetType = 'none' | 'chat' | 'payment' | 'delivery' | 'treatment';

export type Patient = components['schemas']['AdminPatientItem'] & {
  readonly lead_id?: string | null;
  readonly tooth_spacing_map_url: string | null;
  // Returned by GET /admin/patients (latest cycle), but not yet in the
  // generated schema — mirror lead_id / tooth_spacing_map_url handling.
  readonly current_stage_kind?: StageKind | null;
  readonly current_cycle_number?: number | null;
};

export type PatientCreatedResponse = components['schemas']['AdminPatientCreatedResponse'];

export type ChatAdminSummary = {
  readonly admin_id: string;
  readonly user_id: string;
  readonly full_name: string;
  readonly initials: string;
  readonly accent_color: string;
  readonly is_online: boolean;
  readonly avatar_url: string | null;
};

export type ChatMessage = {
  readonly id: string;
  readonly patient_id: string;
  readonly sender_user_id: string;
  readonly sender_role: ChatSenderRole;
  readonly sender_name: string;
  readonly sender_avatar_url: string | null;
  readonly message_text: string;
  readonly message_type: ChatMessageType;
  readonly created_at: string;
  readonly is_edited: boolean;
  readonly attachments: readonly ChatAttachment[];
  readonly reactions?: readonly ChatReaction[];
};

export type ChatReaction = {
  readonly emoji: string;
  readonly count: number;
  readonly reacted_by_me: boolean;
};

export type ChatAttachment = {
  readonly id: string;
  readonly attachment_type: string;
  readonly content_url: string;
  readonly original_filename: string | null;
  readonly mime_type: string | null;
  readonly byte_size: number | null;
  readonly duration_ms: number | null;
  readonly waveform: readonly number[] | null;
};

export type AdminChatListItem = {
  readonly patient_id: string;
  readonly patient_name: string;
  readonly patient_display_id: string | null;
  readonly patient_avatar_url: string | null;
  readonly assigned_admins: readonly ChatAdminSummary[];
  readonly last_message_preview: string;
  readonly last_message_at: string;
  readonly unread_count: number;
  readonly manually_unread?: boolean;
};

export type ChatPageMeta = {
  readonly has_more_before: boolean;
  readonly has_more_after: boolean;
  readonly oldest_cursor: string | null;
  readonly newest_cursor: string | null;
  readonly page_size: number;
};

export type ChatSearchHit = {
  readonly id: string;
  readonly created_at: string;
  readonly snippet: string;
  readonly sender_role: string;
  readonly sender_display_name: string;
};

export type ChatSearchResult = {
  readonly hits: readonly ChatSearchHit[];
  readonly total: number;
  readonly next_cursor: string | null;
  readonly query: string;
};

export type ChatMessageWindow = {
  readonly messages: readonly ChatMessage[];
  readonly page: ChatPageMeta;
};

export type PatientChatThread = {
  readonly patient_id: string;
  readonly patient_name: string;
  readonly patient_display_id: string | null;
  readonly patient_avatar_url: string | null;
  readonly assigned_admins: readonly ChatAdminSummary[];
  readonly messages: readonly ChatMessage[];
  readonly unread_count: number;
  readonly manually_unread?: boolean;
  readonly last_message_at: string | null;
  // Added with keyset pagination; optional so mock/legacy constructors still type.
  readonly last_message_preview?: string | null;
  readonly page?: ChatPageMeta;
};

export type TreatmentStageStep = components['schemas']['AdminTreatmentStageStepItem'];

/** Procedure row embedded in admin treatment cycle payload (OpenAPI `AdminCycleProcedureItem`). */
export type CycleProcedure = components['schemas']['AdminCycleProcedureItem'];

export type BankAccount = {
  readonly id: string;
  readonly account_name: string;
  readonly iban: string;
  readonly bank_name: string;
  readonly swift_code: string | null;
  readonly account_number: string | null;
  readonly currency: string;
  readonly is_active: boolean;
  readonly privacy_offer: string | null;
};

export type PointsRewardType = 'treatment_discount' | 'item';

export type PointsEntryReason = 'manual_grant' | 'redemption' | 'reversal';

export type PointsEntrySource = 'manual' | 'referral_code' | 'system';

/** A row in the admin-managed bonus reward catalog. */
export type PointsReward = {
  readonly id: string;
  readonly name_uk: string;
  readonly name_en: string;
  readonly point_cost: number;
  readonly reward_type: PointsRewardType;
  readonly is_active: boolean;
};

/**
 * One row of the append-only points ledger. `delta_points` is signed: positive
 * for a grant, negative for a redemption. A row with `reversed_by_entry_id` set
 * has already been undone and cannot be reversed again.
 */
export type PointsEntry = {
  readonly id: string;
  readonly patient_id: string;
  readonly delta_points: number;
  readonly reason: PointsEntryReason;
  readonly source: PointsEntrySource;
  readonly note: string | null;
  readonly created_by_admin_id: string | null;
  readonly created_by_admin_name: string | null;
  readonly reward_id: string | null;
  readonly reward_name_uk: string | null;
  readonly reward_name_en: string | null;
  readonly reverses_entry_id: string | null;
  readonly reversed_by_entry_id: string | null;
  readonly created_at: string;
};

export type PointsEntryList = {
  readonly items: readonly PointsEntry[];
  readonly total: number;
  readonly page: number;
  readonly page_size: number;
};

export type PatientPointsSummary = {
  readonly patient_id: string;
  readonly balance_points: number;
  readonly balance_uah: string;
  readonly point_value_uah: string;
  readonly currency: string;
  readonly lifetime_granted_points: number;
  readonly lifetime_redeemed_points: number;
};

/** Serialized `Delivery` row for a cycle (admin treatment payload extension). */
export type CycleDeliverySnapshot = {
  readonly id: string;
  readonly delivery_address_id: string;
  readonly delivery_status: DeliveryStatus;
  readonly tracking_number: string | null;
  readonly shipped_at: string | null;
  readonly delivered_at: string | null;
  readonly received_confirmed_by_patient_at: string | null;
  readonly notes: string | null;
  readonly updated_by_admin_id: string | null;
  readonly created_at: string;
  readonly updated_at: string;
};

/** Snapshot of the address linked to `cycle.delivery` (if any). */
export type CycleDeliveryAddressSnapshot = {
  readonly id: string;
  readonly shipping_method: 'nova_poshta' | 'ukrposhta' | 'international';
  readonly country: string;
  readonly city: string;
  readonly street: string | null;
  readonly house_number: string | null;
  readonly apartment_number: string | null;
  readonly postal_code: string | null;
  readonly recipient_name: string;
  readonly recipient_phone: string;
  readonly is_default: boolean;
  readonly np_settlement_id: string | null;
  readonly np_settlement_name: string | null;
  readonly np_division_id: string | null;
  readonly np_division_name: string | null;
  readonly np_division_number: string | null;
  readonly created_at: string;
  readonly updated_at: string;
};

export type VisualizationMediaItem = {
  readonly id: string;
  readonly media_type: 'photo' | 'video';
  readonly mime_type: string;
  readonly content_url: string;
  readonly original_filename: string | null;
  readonly byte_size: number;
  readonly display_order: number;
};

export type CycleStatus = 'draft' | 'in_progress' | 'completed' | 'cancelled';

// ─── Modeling plan options and 3D meshes ─────────────────────────────────────

export type MeshRole = components['schemas']['MeshRole'];
export type MeshProcessingStatus = components['schemas']['MeshProcessingStatus'];

/** One uploaded STL plus the state of its server-side GLB preview. */
export type MeshAsset = components['schemas']['TreatmentMeshAssetItem'];

/** One tooth-movement step (aligner index) with its per-jaw meshes. */
export type MovementStep = Omit<components['schemas']['MovementStepItem'], 'meshes'> & {
  readonly meshes: readonly MeshAsset[];
};

export type PlanAlignerPricingMode = components['schemas']['AlignerPricingMode'];
export type PlanScheme = components['schemas']['PlanSchemeFile'];
export type PlanAlignerItem = components['schemas']['PlanAlignerItem-Output'];
export type PlanClinicalElement = components['schemas']['PlanClinicalElement'];
export type PlanDiscount = components['schemas']['PlanDiscount-Output'];
export type PlanAttachmentsBlock = components['schemas']['PlanAttachments'];
export type PlanButtonsBlock = components['schemas']['PlanButtons'];
export type PlanElasticsBlock = components['schemas']['PlanElastics'];
export type PlanSeparationBlock = components['schemas']['PlanSeparation'];
export type PlanPricingBreakdown = components['schemas']['PlanPricingBreakdown'];

type GeneratedPlanOption = components['schemas']['PlanOptionItem'];
/** A modeling plan option; the admin view lists published and unpublished alike. */
export type PlanOption = Omit<GeneratedPlanOption, 'meshes'> & {
  readonly meshes: readonly MeshAsset[];
};

/** `pending` and `processing` meshes are still being converted server-side. */
export function isMeshProcessing(mesh: MeshAsset): boolean {
  return mesh.processing_status === 'pending' || mesh.processing_status === 'processing';
}

/**
 * Conversion takes seconds, so this long unsettled means the worker died or the
 * stale-retry job is wedged.
 */
export const MESH_PROCESSING_STALL_MS = 10 * 60 * 1000;

/**
 * True when a mesh has been converting far longer than it should.
 *
 * Uses the row's own `created_at` rather than a client timer, so the verdict
 * survives a page reload and is the same for every admin looking at it. A row
 * without a timestamp is treated as still healthy — the serializer always sends
 * one, and guessing "stalled" would hide a mesh that is about to finish.
 */
export function isMeshStalled(mesh: MeshAsset, now: number): boolean {
  if (!isMeshProcessing(mesh) || !mesh.created_at) return false;
  const startedAt = Date.parse(mesh.created_at);
  return Number.isFinite(startedAt) && now - startedAt > MESH_PROCESSING_STALL_MS;
}

export type Testimonial = {
  readonly id: string;
  readonly rating: number;
  readonly comment: string | null;
  readonly created_at: string;
  readonly patient_id: string;
  readonly patient_name: string;
  readonly patient_display_id: string;
  readonly cycle_number: number;
  readonly stage_title: string | null;
};

export type TestimonialList = {
  readonly items: readonly Testimonial[];
  readonly total: number;
};

export type CycleLabelKind = 'stage' | 'correction';

export type TreatmentStage = Omit<
  components['schemas']['AdminTreatmentStageItem'],
  | 'stages'
  | 'visualization_url'
  | 'latest_payment'
  | 'plan_options'
  | 'movement_steps'
  | 'selected_plan_option_id'
  | 'cycle_label_kind'
> & {
  readonly steps: readonly TreatmentStageStep[];
  readonly stages: readonly TreatmentStageStep[];
  readonly procedures: readonly CycleProcedure[];
  readonly delivery?: CycleDeliverySnapshot | null;
  readonly delivery_address?: CycleDeliveryAddressSnapshot | null;
  /** Lifecycle status of the cycle. */
  readonly status: CycleStatus;
  /** Extended admin cycle payload (may be absent until OpenAPI is regenerated). */
  readonly wearing_started_at?: string | null;
  /**
   * How the cycle chip is named. Cycles are numbered per kind, so a treatment
   * reads "Етап 1, Корекція 1, Етап 2" — never by `cycle_number`.
   */
  readonly cycle_label_kind?: CycleLabelKind;
  readonly visualization_title?: string | null;
  readonly visualization_description?: string | null;
  readonly visualization_media: readonly VisualizationMediaItem[];
  readonly treatment_description?: string | null;
  readonly estimated_treatment_days?: number | null;
  readonly aligner_unit_price?: string | null;
  readonly prepayment_amount?: string | null;
  readonly discount_amount?: string | null;
  readonly additional_items?: readonly { name: string; price: string }[] | null;
  readonly pricing_currency?: string | null;
  /** Main plan first, then alternatives by `sort_order`. */
  readonly plan_options: readonly PlanOption[];
  readonly selected_plan_option_id?: string | null;
  readonly movement_steps: readonly MovementStep[];
};

// Backward-compatible aliases for older client code paths.
export type TreatmentCycleStage = TreatmentStageStep;
export type TreatmentCycle = TreatmentStage;

export type Treatment = Omit<components['schemas']['AdminTreatmentItem'], 'cycles'> & {
  readonly stages: readonly TreatmentStage[];
  readonly cycles: readonly TreatmentStage[];
  readonly diagnosis_titles?: readonly string[] | null;
  readonly patient_features?: readonly string[] | null;
};

export type DeliveryAddress = {
  readonly id: string;
  readonly patient_id: string;
  readonly country: string;
  readonly city: string;
  readonly street: string;
  readonly house_number: string;
  readonly apartment_number: string | null;
  readonly postal_code: string;
  readonly recipient_name: string;
  readonly recipient_phone: string;
  readonly is_default: boolean;
};

export type Delivery = {
  readonly id: string;
  readonly delivery_address_id: string;
  readonly delivery_status: DeliveryStatus;
  readonly tracking_number: string | null;
  readonly shipped_at: string | null;
  readonly delivered_at: string | null;
  readonly notes: string | null;
};

export type Payment = {
  readonly id: string;
  readonly patient_id: string;
  readonly title: string | null;
  readonly amount: number;
  readonly currency: string;
  readonly payment_status: PaymentStatus;
  readonly payment_reference: string | null;
  readonly deadline: string | null;
  readonly confirmed_at: string | null;
  readonly invoice_url: string | null;
  readonly invoice_original_filename: string | null;
};

export type PaginatedResponse<T> = {
  readonly items: readonly T[];
  readonly total: number;
  readonly page: number;
  readonly page_size: number;
};

export type ActivityLogItem = {
  readonly id: string;
  readonly admin_id: string | null;
  readonly admin_name: string | null;
  readonly action: string;
  readonly entity_type: string;
  readonly entity_id: string | null;
  readonly patient_id: string | null;
  readonly patient_name: string | null;
  readonly summary: string;
  readonly created_at: string;
};

export type DashboardStats = {
  readonly total_patients: number;
  readonly active_treatments: number;
  readonly pending_deliveries: number;
  readonly pending_payments: number;
};

export type RecentActivity = {
  readonly id: string;
  readonly patient_name: string;
  readonly patient_id: string;
  readonly action: string;
  readonly timestamp: string;
};

export type NotificationDeliverySummary = {
  readonly channel: NotificationChannel;
  readonly status: NotificationDeliveryStatus;
  readonly provider_message_id: string | null;
  readonly error_message: string | null;
  readonly delivered_at: string | null;
};

export type TaskStatus = 'pending' | 'in_progress' | 'completed' | 'cancelled';
export type TaskPriority = 'low' | 'medium' | 'high' | 'urgent';
export type TaskSource = 'manual' | 'payment_verification';

export type TaskAssignee = {
  readonly admin_id: string;
  readonly first_name: string | null;
  readonly last_name: string | null;
};

export type Task = {
  readonly id: string;
  readonly title: string;
  readonly description: string | null;
  readonly deadline: string | null;
  readonly status: TaskStatus;
  readonly priority: TaskPriority;
  readonly source: TaskSource;
  readonly related_patient_id: string | null;
  readonly related_patient_name: string | null;
  readonly related_payment_id: string | null;
  readonly created_by_admin_id: string | null;
  readonly completed_by_admin_id: string | null;
  readonly completed_at: string | null;
  readonly created_at: string;
  readonly updated_at: string;
  readonly assignees: readonly TaskAssignee[];
};

export type AdminNotificationCategory =
  | 'patient_registered'
  | 'payment_submitted'
  | 'payment_overdue'
  | 'chat_unread'
  | 'final_result_submitted';

export type AdminNotification = {
  readonly id: string;
  readonly category: AdminNotificationCategory;
  readonly title: string;
  readonly body: string;
  readonly related_patient_id: string | null;
  readonly related_cycle_id?: string | null;
  readonly related_payment_id: string | null;
  readonly related_chat_message_id: string | null;
  readonly read_at: string | null;
  readonly created_at: string;
};

export type AdminNotificationListResponse = {
  readonly items: readonly AdminNotification[];
  readonly total: number;
  readonly unread_count: number;
  readonly page: number;
  readonly page_size: number;
};

export type PatientNotification = {
  readonly id: string;
  readonly patient_id: string;
  readonly category: NotificationCategory;
  readonly source: NotificationSource;
  readonly title: string;
  readonly body: string;
  readonly target_type: NotificationTargetType;
  readonly target_id: string | null;
  readonly created_at: string;
  readonly read_at: string | null;
  readonly is_read: boolean;
  readonly delivery_summary: readonly NotificationDeliverySummary[];
};
